import Alert from '@mui/material/Alert';
import { styled, css } from '@mui/material/styles';
import { format } from 'date-fns';
import React from 'react';

import Chip from '~/client/components/base/chip';
import { getDesignTokens } from '~/client/components/base/core';
import { Drawer } from '~/client/components/base/drawer';
import { UserProfileCircle } from '~/client/components/base/icon';
import Text from '~/client/components/base/text';
import { isMultiStakeholder } from '~/client/utils/task-helpers';
import * as GQL from '~/graphql/generated/client';

interface TaskCommentDrawerProps {
  open: boolean;
  onClose: () => void;
  task: GQL.UserTask | null;
  className?: string;
}

const TaskCommentDrawerImpl: React.FC<TaskCommentDrawerProps> = ({
  open,
  onClose,
  task,
  className,
}) => {
  const formatDate = (date: Date | string | number | null | undefined) => {
    if (!date) {
      return '';
    }
    try {
      return format(new Date(date), 'MMM d, yyyy h:mm a');
    } catch {
      return '';
    }
  };

  // Get status label for comment drawer (Approved/Rejected instead of Completed/Rejected)
  const getCommentStatusLabel = (status: GQL.AssignmentStatusType): string => {
    switch (status) {
      case GQL.AssignmentStatusType.Completed:
        return 'Approved';
      case GQL.AssignmentStatusType.Rejected:
        return 'Rejected';
      default:
        return 'Pending';
    }
  };

  const isMulti = task ? isMultiStakeholder(task) : false;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      width={480}
      title={isMulti ? 'Stakeholder Reviews' : 'Task Comments'}
      showCloseButton
    >
      <div className={className}>
        <div className="drawer-content">
          {task && (
            <div className="task-info">
              <Text sz="body14" color="secondary">
                {task.policyName}
              </Text>
              <Text sz="body12" color="secondary">
                {task.assignmentType.charAt(0).toUpperCase() +
                  task.assignmentType.slice(1).toLowerCase()}{' '}
                Task
              </Text>
            </div>
          )}

          <div className="comment-container">
            <Text sz="body16" className="container-title">
              {isMulti ? 'Reviews' : 'Comments'}
            </Text>

            {task && isMulti ? (
              // Multi-stakeholder view
              <div className="message-section">
                {task.assignees?.map((assignee, idx) => (
                  <div key={idx} className="comment-item">
                    <div className="comment-content">
                      <div className="user-info">
                        <div className="user-details">
                          <Text sz="bold-body14">
                            {assignee.assignedToName ?? assignee.assignedTo}
                          </Text>
                          <Text sz="body14">{assignee.assignedTo}</Text>
                          <Chip
                            className="status-chip"
                            data-status={assignee.status}
                            label={
                              <Text sz="body12">
                                {getCommentStatusLabel(assignee.status)}
                              </Text>
                            }
                            variant="filled"
                            size="small"
                          />
                          {assignee.completedAt && (
                            <Text sz="body12" color="textSecondary">
                              {formatDate(assignee.completedAt)}
                            </Text>
                          )}
                        </div>
                        <UserProfileCircle />
                      </div>
                      {assignee.comments && (
                        <div className="comment-wrapper">
                          <Text
                            sz="body14"
                            color="text.primary"
                            className="comment-text"
                          >
                            {assignee.comments}
                          </Text>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Show outcome banner for archived multi-stakeholder tasks */}
                {task.isArchived && task.overallOutcome && (
                  <Alert
                    severity={
                      task.overallOutcome === GQL.AssignmentStatusType.Rejected
                        ? 'warning'
                        : 'success'
                    }
                    className="outcome-alert"
                  >
                    {task.overallOutcome === GQL.AssignmentStatusType.Rejected
                      ? 'Overall outcome is rejected because at least one stakeholder rejected this task'
                      : 'Overall outcome is approved because all stakeholders approved this task'}
                  </Alert>
                )}
              </div>
            ) : task?.comments && task.comments.trim().length > 0 ? (
              // Single stakeholder view
              <div className="message-section">
                <div className="comment-item">
                  <div className="comment-content">
                    <div className="user-info">
                      <div className="user-details">
                        <Text sz="bold-body14">
                          {task.assignedToName ?? task.assignedTo ?? 'Reviewer'}
                        </Text>
                        <Text sz="body14">{task.assignedTo}</Text>
                        {task.completedAt && (
                          <Text sz="body12" color="textSecondary">
                            {formatDate(task.completedAt)}
                          </Text>
                        )}
                      </div>
                      <UserProfileCircle />
                    </div>
                    <div className="comment-wrapper">
                      <Text
                        sz="body14"
                        color="text.primary"
                        className="comment-text"
                      >
                        {task.comments}
                      </Text>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-comments">
                <Text sz="body14" color="secondary">
                  No comments available
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export const TaskCommentDrawer = styled(TaskCommentDrawerImpl)(({ theme }) => {
  const t = getDesignTokens(theme.palette.mode, theme.accentPalette);
  return css`
    .drawer-content {
      padding: ${theme.spacing(4)};

      .task-info {
        margin-bottom: ${theme.spacing(3)};
        padding-bottom: ${theme.spacing(2)};
        border-bottom: 1px solid ${theme.palette.divider};
      }

      .comment-container {
        border: 1px solid ${theme.palette.divider};
        border-radius: 4px;
        padding: ${theme.spacing(4)};
        background-color: ${t.surface1};
        min-height: 180px;

        .container-title {
          margin-bottom: ${theme.spacing(2)};
          font-weight: 500;
        }

        .message-section {
          border: 1px solid ${theme.palette.divider};
          border-radius: 4px;
          padding: ${theme.spacing(2)};
          min-height: 100px;
        }

        .no-comments {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100px;
          border: 1px solid ${theme.palette.divider};
          border-radius: 4px;
        }
      }
    }

    .comment-item {
      padding: ${theme.spacing(2)};
      border: 1px solid ${theme.palette.divider};
      border-radius: 4px;
      margin-bottom: ${theme.spacing(2)};
      background-color: ${t.surface2};

      &:last-of-type {
        margin-bottom: 0;
      }

      .comment-content {
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(2)};

        .user-info {
          display: flex;
          align-items: flex-start;
          gap: ${theme.spacing(2)};
          justify-content: flex-end;

          .user-details {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: ${theme.spacing(0.5)};
          }

          .MuiSvgIcon-root {
            width: 24px;
            height: 24px;
          }
        }

        .comment-wrapper {
          .comment-text {
            padding: ${theme.spacing(1)};
            word-wrap: break-word;
            white-space: pre-wrap;
          }
        }

        .status-chip {
          text-transform: capitalize;
          margin-top: ${theme.spacing(0.5)};

          &[data-status='PENDING'] {
            background-color: ${t.surface2};
            color: ${t.textPrimary};
          }
          &[data-status='COMPLETED'] {
            background-color: ${t.risk.lowBg};
            color: ${t.status.success};
          }
          &[data-status='REJECTED'] {
            background-color: ${t.risk.highBg};
            color: ${t.status.danger};
          }
        }
      }
    }

    .stakeholder-item {
      padding: ${theme.spacing(2)};
      border: 1px solid ${theme.palette.divider};
      border-radius: 4px;
      margin-bottom: ${theme.spacing(2)};
      background-color: ${t.surface2};

      &:last-of-type {
        margin-bottom: 0;
      }

      .stakeholder-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: ${theme.spacing(1)};

        .user-info {
          display: flex;
          align-items: center;
          gap: ${theme.spacing(1.5)};

          .MuiSvgIcon-root {
            width: 24px;
            height: 24px;
          }

          .user-details {
            display: flex;
            flex-direction: column;
          }
        }
      }

      .comment-wrapper {
        margin-top: ${theme.spacing(1)};
        margin-bottom: ${theme.spacing(1)};

        .comment-text {
          padding: ${theme.spacing(1)};
          word-wrap: break-word;
          white-space: pre-wrap;
        }
      }

      .status-chip {
        text-transform: capitalize;

        &[data-status='PENDING'] {
          background-color: ${t.surface2};
          color: ${t.textPrimary};
        }
        &[data-status='COMPLETED'] {
          background-color: ${t.risk.lowBg};
          color: ${t.status.success};
        }
        &[data-status='REJECTED'] {
          background-color: ${t.risk.highBg};
          color: ${t.status.danger};
        }
      }
    }

    .outcome-alert {
      margin-top: ${theme.spacing(2)};
    }
  `;
});

export default TaskCommentDrawer;
